import {
  ApolloClient,
  InMemoryCache,
  from,
  NormalizedCacheObject,
  Operation,
  HttpLink,
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'

import { LogoutOptions } from '@auth0/auth0-react'
import { getAuthConfig } from '@babylon/babylon-auth'
import { onError } from '@apollo/client/link/error'
import {
  setRequestHeaders,
  errorParsingAfterware,
  errorLoggingAfterware,
  flashErrorAfterware,
} from '@/middleware'
import { ParsedGraphQLError } from '@/types/graphql'

let client: ApolloClient<NormalizedCacheObject>

const getBatchKey = (apolloUri: string) => (operation: Operation) => {
  const context = operation.getContext()

  const contextConfig = {
    http: context.http,
    options: context.fetchOptions,
    credentials: context.credentials,
    // babylon-request-id is unique on each request
    // we must remove its value here to ensure queries get batched
    headers: { ...context.headers, 'babylon-request-id': null },
  }

  const uri = context.uri || apolloUri

  return uri + JSON.stringify(contextConfig)
}

const getStatusCode = (errors: ParsedGraphQLError[]) => {
  const { logoutStatusCodes } = getAuthConfig()

  const errorCodes = errors?.find((error: ParsedGraphQLError) =>
    logoutStatusCodes.includes(error.status!)
  )

  return errorCodes
}

const init = (
  apolloUri: string,
  appName: string,
  acceptLanguage: string,
  logout: (options?: LogoutOptions) => void
) => {
  const httpLinkConfig = {
    uri: apolloUri,
    credentials: 'include',
  }

  const httpLink = new BatchHttpLink({
    batchKey: getBatchKey(apolloUri),
    ...httpLinkConfig,
  })

  const cache = new InMemoryCache()

  client = new ApolloClient({
    ssrMode: false,
    link: from([
      setRequestHeaders(appName, acceptLanguage),
      flashErrorAfterware,
      onError(({ graphQLErrors }) => {
        const graphQlCallUnauthorised = getStatusCode(
          graphQLErrors as ParsedGraphQLError[]
        )

        if (!window.Cypress && graphQlCallUnauthorised) {
          logout()
        }
      }),
      errorLoggingAfterware,
      errorParsingAfterware,
      ...(!window.Cypress ? [httpLink] : [new HttpLink(httpLinkConfig)]),
    ]),
    cache,
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
    },
  })

  return client
}

const getClient = () => client

export { init, getClient }
